import type { FloatingAlign, FloatingPlacement, FloatingSide } from '@/lib/floating';
import { placeFloating } from '@/lib/floating';
import { useLayoutEffect, useState } from 'react';

/**
 * Keeping a floating panel where it belongs, for as long as it is open (issue
 * #104).
 *
 * `lib/floating.ts` answers WHERE a panel goes, in numbers, without touching a
 * DOM. This is the other half: measuring the anchor and the panel, and asking
 * that question again every time one of the three inputs moves.
 *
 * ## Why it exists, and it is not a preference
 *
 * `lib/floating.ts` opens by saying that two placers kept in step by hand are
 * one that drifts. By the end of issue #104 there were FOUR near-identical
 * placement effects - the drawn bubble, the fiche of a criterion, the fiche of
 * a variable, the criterion chooser - and the drift had already happened: a
 * defect found on a capture was fixed in one of them and left standing in the
 * other three. This module is that lesson applied to the effect rather than to
 * the maths.
 *
 * ## The three things that move, and the third is the one that was missed
 *
 * 1. The **window**, on `resize`.
 * 2. The **anchor**, on `scroll` - captured, because an anchor can live in a
 *    panel that scrolls for its own account, and an inner scroll never reaches
 *    `window` without capture.
 * 3. The **panel itself**. A fiche is placed while it still reads « Lecture du
 *    critère… » - some sixty pixels - decides then that it fits under its
 *    anchor, and is four times taller a moment later when the read lands. It
 *    then hangs off the bottom of the window with its way out unreachable, and
 *    NEITHER of the first two listeners fires: nothing moved but the panel.
 *    That is what the `ResizeObserver` is for, and it is the whole reason this
 *    file exists.
 *
 * `ResizeObserver` is absent from jsdom, so the guard around it is not
 * defensive style: without it every test mounting a panel would throw.
 */

export interface FloatingPlacementRequest {
  /** The element the panel points at. `null` while it has not mounted. */
  readonly anchor: HTMLElement | null;
  /**
   * The panel's width when the caller fixes it, which the two fiches do.
   *
   * `null` means « measure it », which is what a bubble whose width follows its
   * text needs. Measured, it costs one extra pass: the first placement runs at
   * zero and `placeFloating` answers the preferred side without flipping, which
   * is the right guess for that single frame.
   */
  readonly width: number | null;
  readonly side: FloatingSide;
  readonly align?: FloatingAlign;
}

export interface FloatingPlacementState {
  readonly at: FloatingPlacement;
  /** To put on the panel's own node, so its size can be watched. */
  readonly panelRef: (node: HTMLElement | null) => void;
}

const NOWHERE: FloatingPlacement = { left: 0, top: 0, side: 'top', arrow: 0 };

export function useFloatingPlacement({
  anchor,
  width,
  side,
  align = 'center',
}: FloatingPlacementRequest): FloatingPlacementState {
  const [panel, setPanel] = useState<HTMLElement | null>(null);
  const [at, setAt] = useState<FloatingPlacement>(NOWHERE);

  useLayoutEffect(() => {
    if (anchor === null) return;
    const measure = (): void => {
      setAt(
        placeFloating({
          anchor: anchor.getBoundingClientRect(),
          floating: {
            width: width ?? panel?.offsetWidth ?? 0,
            height: panel?.offsetHeight ?? 0,
          },
          viewport: { width: window.innerWidth, height: window.innerHeight },
          side,
          align,
        }),
      );
    };
    measure();

    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);

    let observer: ResizeObserver | null = null;
    if (panel !== null && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(panel);
    }

    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [anchor, panel, width, side, align]);

  return { at, panelRef: setPanel };
}
